// Item 268: shared client-side parser for the RFC 7807 problem+json
// error bodies the backend has written since item 241
// (api/internal/problem/problem.go). Callers that used to throw
// `new Error(${res.status} ${res.statusText})` or `new Error(await
// res.text())` on a failed fetch got either a bare status line or the
// raw JSON blob as the Error message; this normalizes both into a
// human-readable `.message` (so existing `String(error)` /
// `error.message` call sites improve for free) while still exposing
// the structured fields for callers that want them.
export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  request_id?: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly title?: string;
  readonly type?: string;
  readonly instance?: string;
  readonly requestId?: string;

  constructor(status: number, message: string, problem?: ProblemDetails) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.title = problem?.title;
    this.type = problem?.type;
    this.instance = problem?.instance;
    this.requestId = problem?.request_id;
  }
}

// parseErrorResponse reads a failed fetch Response and returns an
// ApiError whose `.message` is the problem+json `detail` (falling
// back to `title`, then the raw body, then the status line) so it
// reads as a sentence instead of a status code.
export async function parseErrorResponse(res: Response): Promise<ApiError> {
  const text = await res.text();
  let problem: ProblemDetails | undefined;
  try {
    const parsed: unknown = JSON.parse(text);
    if (
      parsed &&
      typeof parsed === 'object' &&
      (typeof (parsed as ProblemDetails).detail === 'string' ||
        typeof (parsed as ProblemDetails).title === 'string')
    ) {
      problem = parsed as ProblemDetails;
    }
  } catch {
    // Body isn't JSON (e.g. a wire-protocol error, or a proxy's HTML
    // error page) — fall through to the raw text / status line below.
  }
  const message =
    problem?.detail || problem?.title || text || `${res.status} ${res.statusText}`;
  return new ApiError(res.status, message, problem);
}
